import { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import useCallbackAsync from 'hooks/useCallbackAsync';
import confirm from 'modules/confirm';
import { getArrayLength, getHasElementsInArray } from 'utils';
import dataRoomApi from '../dataRoomApi';
import {
  DataRoomRFICategory,
  RfiSendReminderTrigger,
} from '../models/DataRoomRfi';
import RfiContext from '../providers/Rfi/Rfi.context';
import DataRoomContext from '../providers/DataRoom/DataRoom.context';
import { getCategoryWasReminderSentRecently } from '../subrouters/RFI/pages/RfiDetails/utils';

const useRfiSendCategoriesReminder = (
  categories: DataRoomRFICategory[],
  trigger: RfiSendReminderTrigger,
  onCategoryUpdate: (category: DataRoomRFICategory) => void,
  onSent?: () => void,
) => {
  const { t } = useTranslation();

  const { showActionUndo } = useContext(RfiContext);
  const {
    dataRoom: { _id: dataRoomId },
  } = useContext(DataRoomContext);

  const categoryIds = categories.map(({ _id }) => _id);

  const recipientEmails = Array.from(
    new Set(categories.map(({ assignedTo }) => assignedTo).filter(Boolean)),
  );
  const count = getArrayLength(recipientEmails);

  const onUndoSendCategoriesReminder = useCallback(async () => {
    await dataRoomApi.rfi.undoSendCategoryReminder(dataRoomId, categoryIds);

    categories.forEach(onCategoryUpdate);
  }, [dataRoomId, categoryIds, categories, onCategoryUpdate]);

  const [sendCategoriesReminder, loading] = useCallbackAsync(async () => {
    const confirmed = await confirm({
      title: t('sendReminderConfirmTitle', {
        count,
        email: recipientEmails[0],
      }),
      content: t('sendReminderConfirmDescription', { count }),
      submitBtnText: t('sendReminder'),
    });

    if (!confirmed) return;

    await dataRoomApi.rfi.sendCategoryReminder(
      dataRoomId,
      categoryIds,
      trigger,
    );

    categories.forEach((category) =>
      onCategoryUpdate({
        ...category,
        reminders: [
          ...(category.reminders || []),
          { email: category.assignedTo, sentAt: new Date() },
        ],
      }),
    );

    onSent?.();

    showActionUndo(
      t('sendRemindersSuccess', { count }),
      onUndoSendCategoriesReminder,
    );
  });

  return {
    sendCategoriesReminder,
    loading,
    hasRecipient:
      getHasElementsInArray(recipientEmails) &&
      !categories.every(getCategoryWasReminderSentRecently),
  };
};

export default useRfiSendCategoriesReminder;
